import { Portal } from '@radix-ui/react-portal'
import { Slot } from '@radix-ui/react-slot'
import clsx from 'clsx'
import { X } from 'lucide-react'
import React, { useEffect, useId, useState } from 'react'

interface RightDrawerProps {
  trigger?: React.ReactNode;
  children:
    | React.ReactNode
    | (({ dismissDrawer }: { dismissDrawer: () => void }) => React.ReactNode);
  width?: 'small' | 'medium' | 'large' | 'xl' | '2xl' | 'full';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
}

function getWidthClassNames (width: RightDrawerProps['width']) {
  switch (width) {
    case 'small':
      return 'w-80' // 20rem
    case 'medium':
      return 'w-96' // 24rem
    case 'large':
      return 'w-[30rem]' // 30rem
    case 'xl':
      return 'w-[40rem]' // 40rem
    case '2xl':
      return 'w-[48rem]' // 48rem
    case 'full':
      return 'w-full'
    default:
      return 'w-[30rem]' // default to large
  }
}

export function RightDrawer ({ trigger, children, width = 'medium', open: controlledOpen, onOpenChange, onClose }: RightDrawerProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const id = useId()

  // Use controlled state if provided, otherwise use internal state
  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen

  const setIsOpen = (newOpen: boolean) => {
    if (isControlled) {
      onOpenChange?.(newOpen)
    } else {
      setUncontrolledOpen(newOpen)
    }
  }

  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'

      return () => {
        document.body.style.overflow = originalOverflow
      }
    }
  }, [isOpen])

  function renderChildren () {
    if (typeof children === 'function') {
      return children({
        dismissDrawer: () => {
          setIsOpen(false)
          onClose?.()
        }
      })
    }

    return children
  }

  const widthClassName = getWidthClassNames(width)

  return (
    <>
      {trigger && (
        <Slot
          aria-controls={id}
          aria-haspopup='dialog'
          aria-expanded={isOpen}
          onClick={() => {
            setIsOpen(true)
          }}
        >
          {trigger}
        </Slot>
      )}
      <Portal>
        {/* Overlay */}
        <div
          id={id}
          className={clsx('fixed inset-0 transition duration-300', {
            'pointer-events-none bg-black/0': !isOpen,
            'bg-black/40': isOpen,
          })}
          onClick={() => {
            setIsOpen(false)
            onClose?.()
          }}
          role='dialog'
        />
        {/* Drawer */}
        <div
          className={clsx(
            'fixed top-0 right-0 bottom-0 bg-white transition-all duration-300',
            widthClassName,
            {
              'translate-x-full invisible': !isOpen,
              'translate-x-0': isOpen,
            }
          )}
        >
          <div
            className={clsx('flex flex-col h-full transition duration-300 px-10 py-8', {
              'opacity-0': !isOpen,
              'opacity-100': isOpen,
            })}
          >
            <div className='h-12 flex items-end justify-end pb-2'>
              <button
                onClick={() => {
                  setIsOpen(false)
                  onClose?.()
                }}
                className='p-1 hover:bg-gray-100 rounded-md transition-colors'
                aria-label='Close drawer'
              >
                <X size={24} className='text-gray-600' />
              </button>
            </div>
            <div className='flex-1 overflow-y-auto pr-10 -mr-10'>
              {renderChildren()}
            </div>
          </div>
        </div>
      </Portal>
    </>
  )
}
